// src/store/signup_slice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "./store";
import apiClient from "../services/api_client";
import ToastService from "../common/toast/toast_service";

// Interface for the signup request payload
interface SignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface SignUpState {
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: SignUpState = {
  loading: false,
  error: null,
  success: false,
};

const signupSlice = createSlice({
  name: "signup",
  initialState,
  reducers: {
    signUpStart(state) {
      state.loading = true;
      state.error = null;
      state.success = false;
    },
    signUpSuccess(state) {
      state.loading = false;
      state.error = null;
      state.success = true;
    },
    signUpFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    },
    resetSignUpState(state) {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
});

export const { signUpStart, signUpSuccess, signUpFailure, resetSignUpState } =
  signupSlice.actions;

export const signUp = (values: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
}) => async (dispatch: AppDispatch) => {
  dispatch(signUpStart());
  try {
    const signupData: SignUpRequest = {
      email: values.email,
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber
    };

    await apiClient.post("/users/register", signupData);
    
    dispatch(signUpSuccess());
    ToastService.showSuccess("Registration successful! Please login.");
    
    return { success: true };
  } catch (error: unknown) {
    let errorMessage = "Registration failed";
    
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { message?: string; error?: string } } };
      errorMessage = axiosError.response?.data?.message || 
                    axiosError.response?.data?.error || 
                    errorMessage;
    } else if (error instanceof Error) {
      errorMessage = error.message || errorMessage;
    }
                        
    dispatch(signUpFailure(errorMessage));
    ToastService.showError(errorMessage);
    return { error: errorMessage };
  }
};

export default signupSlice.reducer;
